import React, { useState, useEffect } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Button,
    Alert,
    CircularProgress,
    Container
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../users/AuthContext';
import { APIApplicationFormListById } from '../../utilities/APIs';
import ApplicationForm from '../ApplicationForm/ApplicationForm';
import useCustomToast from '../../hooks/useToast';

const ApplicantFormView = () => {
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { formId } = useParams();
    const location = useLocation();
    const { makeAuthenticatedRequest, user, logout } = useAuth();
    const showToast = useCustomToast();

    useEffect(() => {
        // Check if form data was passed via navigation state
        if (location.state?.formData) {
            setFormData(location.state.formData);
            setLoading(false);
        } else if (formId) {
            fetchFormData();
        } else {
            setError('No form specified');
            setLoading(false);
        }
    }, [formId, location.state]);

    const fetchFormData = async () => {
        try {
            setLoading(true);
            const response = await makeAuthenticatedRequest(APIApplicationFormListById + formId);
            
            if (response.data) {
                setFormData(response.data);
            } else {
                setError('Form not found');
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load application form');
            showToast('Error', 'Failed to load application form', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/applicant/forms');
    };

    const handleCancel = () => {
        navigate('/applicant/forms');
    };

    const handleLogout = () => {
        logout();
        navigate('/applicant/login');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50' }}>
                <AppBar position="static">
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleBack}
                            sx={{ mr: 2 }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Application Form
                        </Typography>
                        <Button color="inherit" onClick={handleLogout}>
                            Logout
                        </Button>
                    </Toolbar>
                </AppBar>
                
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                    <Button variant="contained" onClick={handleBack}>
                        Back to Forms
                    </Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50' }}>
            {/* Header */}
            <AppBar position="static" sx={{ backgroundColor: 'primary.main' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {formData?.name || formData?.formName || 'Application Form'}
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                        {user?.firstName || user?.username}
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 4 }}>
                <ApplicationForm 
                    formId={formId}
                    onCancel={handleCancel}
                    demoData={formData}
                />
            </Container>
        </Box>
    );
};

export default ApplicantFormView;
